import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { ConnectionState, ServerEventMessage } from '../../models';
import { ChunkedServerEventMessage, PUSHER_CHUNK_MESSAGE_TYPE } from '../../models/chunked-messages.model';
import { PusherClientService } from '../pusher-client/pusher-client.service';
import { AbstractPusherConfigService } from '../abstract-pusher-config/abstract-pusher-config.service';
import { ServerEventsProviderService } from '../server-events-provider-base/server-events-provider-base';

interface PendingChunkedMessage {
  totalChunks: number;
  currentReceivedChunks: number;
  partialData: string[];
}

@Injectable()
export class ServerEventsProviderImpl extends ServerEventsProviderService {
  private isInitialized: boolean;
  private readonly serverEvents$ = new Subject<ServerEventMessage>();
  private readonly pendingChunkedMessages: Record<string, PendingChunkedMessage> = {};

  constructor(
    private pusherClient: PusherClientService,
    private config: AbstractPusherConfigService,
  ) {
    super();
  }

  getServerEventsFeed(): Observable<ServerEventMessage> {
    this.initCache();
    return this.serverEvents$;
  }

  getServerEvent(messageType: string): Observable<ServerEventMessage> {
    return this.getServerEventsFeed().pipe(
      filter((ServerEventMessage) => ServerEventMessage.message_type === messageType),
    );
  }

  getConnectionState(): Observable<ConnectionState> {
    return this.pusherClient.getConnectionState();
  }

  getLogs(): Observable<any> {
    return this.pusherClient.getLogs();
  }

  private initCache(): void {
    if (!this.isInitialized) {
      new Observable<ServerEventMessage>((subscriber) => {
        const subscription = this.config
          .getChannelIdAsync()
          .pipe(
            filter((channelId) => !!channelId),
            switchMap((channelId) => {
              return this.pusherClient.getClient().pipe(
                tap((pusherClient) => {
                  const channel = pusherClient.subscribe(`private-${channelId}`);
                  channel.bind('pusher:subscription_error', (error) => {
                    if (error['type'] === 'AuthError') {
                      // Do nothing
                    } else {
                      // eslint-disable-next-line no-console
                      console.error('Pusher connection error. Reconnecting...');
                      pusherClient.disconnect();
                      pusherClient.connect();
                    }
                  });

                  channel.bind('notification', (data: ServerEventMessage) => {
                    if (data) {
                      subscriber.next(data);
                    }
                  });
                }),
              );
            }),
          )
          .subscribe();

        return () => subscription.unsubscribe();
      }).subscribe((serverEventMessage) => {
        if (serverEventMessage.message_type === PUSHER_CHUNK_MESSAGE_TYPE) {
          this.handleChunkedMessage(serverEventMessage as ChunkedServerEventMessage);
          return;
        }

        this.serverEvents$.next(serverEventMessage);
      });

      this.isInitialized = true;
    }
  }

  protected handleChunkedMessage(serverEventMessage: ChunkedServerEventMessage): void {
    const { chunked_message_id, chunk_index, total_chunks, chunk_data } = serverEventMessage.message_object;

    if (!(chunked_message_id in this.pendingChunkedMessages)) {
      this.initializePendingMessage(chunked_message_id, total_chunks);
    }

    this.updateChunkData(chunked_message_id, chunk_index, chunk_data);

    const pendingMessage = this.pendingChunkedMessages[chunked_message_id];
    if (pendingMessage.currentReceivedChunks === pendingMessage.totalChunks) {
      this.processCompleteChunkedMessage(chunked_message_id);
    }
  }

  private initializePendingMessage(messageId: string, totalChunks: number): void {
    this.pendingChunkedMessages[messageId] = {
      totalChunks,
      currentReceivedChunks: 0,
      partialData: [],
    };
  }

  private updateChunkData(messageId: string, chunkIndex: number, chunkData: string): void {
    const pendingMessage = this.pendingChunkedMessages[messageId];
    pendingMessage.currentReceivedChunks++;
    pendingMessage.partialData[chunkIndex] = chunkData;
  }

  private processCompleteChunkedMessage(messageId: string): void {
    const pendingMessage = this.pendingChunkedMessages[messageId];
    const fullMessage = pendingMessage.partialData.join('');

    try {
      this.serverEvents$.next(JSON.parse(fullMessage));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error parsing chunked message', err, fullMessage);
    }

    delete this.pendingChunkedMessages[messageId];
  }
}
